/**
 * Routes for the product shell. The shell is the app: paths sit at the root.
 *
 *   /                     home dashboard
 *   /drawings             parts + BOMs catalog
 *   /drawings/parts|boms|review|upload  a named view of that catalog
 *   /drawings/:jobId          one BOM / assembly
 *   /drawings/:jobId/:itemNo  one part inside it
 *   /rfq                  RFQ module
 *   /rfq/new              intake (mail + files)
 *   /rfq/:id              one RFQ; the page opens on Files
 *   /rfq/:id/:step        a journey step (understand/quote/cost still alias)
 *   /customers            customers grouped from RFQ emails
 *   /customers/:email     one customer; the key is the email
 *   /erp                  stock and materials
 *   /production           work orders
 *   /production/:id       one work order
 *   /ai                   AI assistant
 *   /agents               agents, coming soon
 *   /integrations         integrations, coming soon (no connectors yet)
 *   /profile              settings
 *   /profile/shop         shop profile tab
 *   /profile/machines     Machines tab, Add machine dialog open
 *   /profile/labour       labour types tab
 *   /profile/processes    Process recipes tab (op library editor)
 *
 * A leading `/v2` is still accepted so an old bookmark lands on the same page.
 * Any other first segment (`/orders`, `/cost`) is not a page: the shell shows
 * a 404 instead of the home dashboard.
 */

/** The shell is the whole app. Kept so older checks compile. */
export const AT_ROOT = true;

export const PREFIX = "";

/** Drop a legacy `/v2` prefix. `/v2` and `/v2/erp` become `/` and `/erp`. */
export function stripLegacyPrefix(pathname: string): string {
  if (pathname === "/v2") return "/";
  if (pathname.startsWith("/v2/")) return pathname.slice(3);
  return pathname;
}

export const PAGES = [
  "home",
  "drawings",
  "rfq",
  "customers",
  "erp",
  "production",
  "ai",
  "agents",
  "integrations",
  "profile",
] as const;

export type Page = (typeof PAGES)[number];

/**
 * Second-segment words under /drawings that name a view rather than a job.
 *
 * Home deep-links into the catalog ("Parts", "BOMs", "Upload"), and a view has
 * no record id to sit in front of it. Engine job ids are opaque handles, so a
 * job can only collide with one of these if it is literally named "parts".
 */
export const DRAWING_VIEWS = ["upload", "parts", "boms", "review"] as const;

export type DrawingView = (typeof DRAWING_VIEWS)[number];

function isDrawingView(raw: string | null): raw is DrawingView {
  return !!raw && (DRAWING_VIEWS as readonly string[]).includes(raw);
}

/** A known shell page, or a path the router does not recognize. */
export type RoutePage = Page | "notFound";

export type Route = {
  page: RoutePage;
  /** Record id from the second segment, e.g. the RFQ under `/v2/rfq/:id`. */
  id: string | null;
  /** Third segment, e.g. the part under `/v2/drawings/:jobId/:itemNo`. */
  sub: string | null;
};

/** Every path belongs to this shell. */
export function isAppPath(_pathname: string = window.location.pathname): boolean {
  return true;
}

export function parseRoute(
  pathname: string = window.location.pathname,
): Route {
  const rest = stripLegacyPrefix(pathname).replace(/^\/+|\/+$/g, "");
  if (!rest) return { page: "home", id: null, sub: null };
  const [head, second, third] = rest.split("/");
  const page = PAGES.find((p) => p === head);
  if (!page) return { page: "notFound", id: null, sub: null };
  // A malformed escape is not an id — fall back to the bare page.
  const decode = (raw: string | undefined): string | null => {
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return null;
    }
  };
  const id = decode(second);
  if (page === "drawings" && isDrawingView(id)) {
    return { page, id: null, sub: id };
  }
  return { page, id, sub: id ? decode(third) : null };
}

export function pageHref(
  page: Page,
  id?: string | null,
  sub?: string | null,
): string {
  // At the root the home page is "/", not the empty string.
  const base = page === "home" ? PREFIX || "/" : `${PREFIX}/${page}`;
  if (!id) {
    return page === "drawings" && isDrawingView(sub ?? null) ? `${base}/${sub}` : base;
  }
  const withId = `${base}/${encodeURIComponent(id)}`;
  return sub ? `${withId}/${encodeURIComponent(sub)}` : withId;
}

export function pushRoute(
  page: Page,
  id?: string | null,
  sub?: string | null,
): void {
  const href = pageHref(page, id, sub);
  if (window.location.pathname === href) return;
  window.history.pushState({}, "", href + window.location.search);
}
